import React, { useState, useMemo, useEffect } from 'react';
import styles from './ResponsiveCard.module.scss';
import { SllLesson } from '../../../../types';
import { getMediaAbsolutePath } from '../../../../services/File';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import TimerIcon from 'src/assets/icons/timer.svg?react';
import FlagAU from 'src/assets/icons/flag_au.svg?react';
import FlagCA from 'src/assets/icons/flag_ca.svg?react';
import FlagUS from 'src/assets/icons/flag_us.svg?react';
import { categoryStyleOptions, categoryTiersOptions, countryOptions, IconInfo } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon';


type Props = {
  lesson: SllLesson
  handleEditLesson: (lesson: SllLesson) => void
  handleOpenLesson: (lesson: SllLesson) => void
}

const getFlagIcon = (type: String) => {
  switch (type) {
    case "Australia": {
      return <FlagAU />;
    }
    case "Canada": {
      return <FlagCA />;
    }
    case "USA": {
      return <FlagUS />;
    }
    case "Canada & USA": {
      return <><FlagCA /><FlagUS /></>;
    }
    case "All Countries": {
      return <><FlagAU /><FlagCA /><FlagUS /></>;
    }
    default: {
      return null;
    }
  }
}

function TagButton ({title}){
  if (!title) return null;
  return (
    <div className={styles.tagContainer}>
        <p className={styles.tagTitle}>{title}</p>
    </div>
  )
}

function ResponsiveCard(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { lesson, handleOpenLesson, handleEditLesson } = props;
  const {
    categories,
    competency,
    isExtensionAvailable,
    lessonDetails,
  } = lesson;

  const lessonCategories = useMemo(() => JSON.parse(categories || '[]'), [categories]);
  const skill = useMemo(() => JSON.parse(competency || '[]'), [competency]);

  const [tiers, setTiers] = useState('')
  const [mood, setMood] = useState('')
  const [style, setStyle] = useState('')
  const [auSkill, setAuSkill] = useState('')

  useEffect(() => {
    if (lessonCategories['Tiers']) {
      // TO DO: currently, we have bad datas for the Tiers in the DB. We have to udpate the Tiers manually at the edit-lesson
      // After DB update, we should revert to the prev version that is commented out.
      const tiersOption = categoryTiersOptions.find(o => (`${o.value}` === `${lessonCategories['Tiers']}` || o.oldValues?.includes(lessonCategories['Tiers'])))
      setTiers(tiersOption?.value || '')
      // setTiers(lessonCategories['Tiers'])
    }
    if (lessonCategories['Mood']) {
      setMood(lessonCategories['Mood'])
    }
    if (lessonCategories['Style']) {
      // TO DO: currently, we have bad datas for the style options in the DB. We have to udpate the style manually at the edit-lesson
      // After DB update, we should revert to the prev version that is commented out.
      const styleOption = categoryStyleOptions.find(o => (`${o.value}` === `${lessonCategories['Style']}` || o.oldValues?.includes(lessonCategories['Style'])))
      setStyle(styleOption?.value || '')
      // setStyle(lessonCategories['Style'])
    }
    if (lessonCategories['Skill']) {
      setAuSkill(lessonCategories['Skill'])
    }
  }, [lessonCategories])

  return (
    <div className={`${isHovered ? styles.card + ' ' + styles.cardHovered : styles.card}`} onClick={() => handleOpenLesson?.(lesson)} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      >
        <img src={lesson.coverImage ? getMediaAbsolutePath(lesson.coverImage) : ''} width={'100%'} height={140} alt="Lesson Cover"/>
        {((lesson.country === countryOptions[1].label && props?.lesson?.suggestedDuration) || (lesson.country !== countryOptions[1].label && props?.lesson?.duration)) ? (
          <div className={styles.timerContainer}>
              <span className={styles.timerIcon}><TimerIcon /></span>
              <p className={styles.timerTitle}>{lesson.country === countryOptions[1].label ? props?.lesson?.suggestedDuration : props?.lesson?.duration} min</p>
          </div>
        ) : null}
        <div className={styles.countryContainer}>
          {getFlagIcon(`${lesson.country}`)}
        </div>
      <div className={styles.iconView}>
        <div className={styles.editIconWrapper}>
          <IconPencil onClick={() => handleEditLesson(lesson)}/>
        </div>
      </div>
      <div className={styles.progressBackground}>
        <div className={styles.progressBar} style={{ width: `${props?.lesson?.progress || 0}%`, backgroundColor: '#62C590', height: '8px' }}></div>
      </div>
      <div className={styles.titleContainer}>
        <p className={styles.title}>{lesson?.name || ''}</p>
        {lesson.lessonType === '5' ? <div className={styles.tagView}>
          <TagButton title="Downloadable Resource" />
        </div> : <>
        {(auSkill || skill.length > 0) && <div className={styles.tagView}>
          {lesson.country === countryOptions[1].label ? <TagButton title={auSkill} /> : <TagButton title={skill[0]} />}
        </div>}
        <span className={styles.splitor} />
        {lesson.lessonType === '4' ? <div className={styles.onboardingDetailView}>
          <LordIcon src={IconInfo}
            isHover={true} 
            colors={{
              primary: '#AF77B8',
              secondary: '#FFB073'
            }}
            size={20}
            target='.CoverUnit_tagBox__nusN2' />
          <span>{lessonDetails}</span>
        </div> : <div className={styles.tagView}>
          <TagButton title={mood} />
          <TagButton title={style} />
          <TagButton title={tiers} />
          {isExtensionAvailable && <TagButton title='Extension' />}
        </div>}
        </>}
      </div>
    </div>
  );
}

export default ResponsiveCard

